import * as PIXI from 'pixi.js';
import { dialogConfig } from '@/config/DialogConfig';
import { SoundManager } from '@/systems/SoundManager';
import { StoryManager } from '@/systems/StoryManager';
import { InputManager } from '@/systems/InputManager';
import type { DialogChoice, DialogNode, DialogTree } from '@/types';
import Hidash from "@/utils/Hidash";
import {InventoryDisplay} from "@/ui/InventoryDisplay.ts";
import {itemsConfig} from "@/config/ItemsConfig.ts";
import {ClickableNode} from "@/types/DialogTypes";
// import {Game} from "@/core";
import { GifSprite } from 'pixi.js/gif';
import gsap from 'gsap';
import {Game} from "@/core";
import { CanvasTextMetrics } from 'pixi.js';

// Interface for queued actions that should be executed after dialog closes
interface QueuedAction {
  type:
    | 'addPoints'
    | 'deductPoints'
    | 'addAffection'
    | 'addHostility'
    | 'deductAffection'
    | 'deductHostility'
    | 'nextDay'
    | 'previousDay'
    | 'addState'
    | 'removeState'
    | 'addCustomValue'
    | 'custom';
  value?: any;
  customFunction?: () => void;
}

let previousDialogTimeoutId:number|undefined

export class DialogSystem {
  private container: PIXI.Container;
  private cutsceneContainer: PIXI.Container;
  private clickableContainer:PIXI.Container;
  private backgroundSprite: PIXI.Sprite | null = null;
  private characterSprite!: PIXI.Sprite | null = null;
  private characterContainer: PIXI.Container; // New container for characters above dialog box
  private background!: PIXI.Graphics;
  private dialogBoxSprite: PIXI.Sprite | null = null;
  private dialogBoxTexture: PIXI.Texture | null = null;
  private choiceSelectorTexture: PIXI.Texture | null = null;
  private choiceGradientTexture: PIXI.Texture | null = null;
  private interactionArea!: PIXI.Graphics; // Invisible overlay for interactions
  private nameText!: PIXI.Text;
  private dialogText!: PIXI.Text;
  private cinematicText!: PIXI.Text;
  private choicesContainer!: PIXI.Container;
  private continueText!: PIXI.Text;
  private isVisible: boolean = false;
  private screenWidth: number;
  private screenHeight: number;
  private currentDialogTree: DialogTree | null = null;
  private currentNodeId: string | null = null;
  private choiceTexts: PIXI.Text[] = [];
  private choiceButtons: PIXI.Container[] = [];
  private choiceBackgrounds: PIXI.Graphics[] = [];

  private exitInteractionModeText: PIXI.Text | undefined;
  private cinematicNextText: PIXI.Text | undefined;

  // Track current NPC for interaction state management
  private currentNPCName: string | null = null;
  private currentCharacterImage: string | null = null;
  private previousCharacterImage: string | null = null;

  // Action queue system
  private actionQueue: QueuedAction[] = [];

  // Performance optimizations
  private lastDialogSize: { width: number; height: number } = { width: 0, height: 0 };
  private choiceTextPool: PIXI.Text[] = [];
  private choiceButtonPool: PIXI.Container[] = [];
  private choiceBackgroundPool: PIXI.Graphics[] = [];
  private readonly maxChoicePoolSize = 10;
  private animationId: number | null = null;
  private isTyping:boolean|undefined;

  private game:Game;

  private animationInterval:number;

  // Image loading cache
  private imageCache: Map<string, PIXI.Texture> = new Map();

  public setGame(game:Game){
    this.game = game
  }

  // Dialog padding configuration
  private readonly topPadding = 60; // Extra padding at top to position text lower

  private get currentTopPadding(): number {
    const hasChoices = this.choicesContainer && this.choicesContainer.children.length > 0;
    return hasChoices ? -10 : this.topPadding;
  }

  private get currentButtonAreaStartX(): number {
    const isMobile = (this.screenWidth < 768 || InputManager.getInstance().isMobile());
    return isMobile ? 20 : 100;
  }

  // Hidden timeout choice management
  private choiceTimeoutId: number | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    this.container = new PIXI.Container();

    this.container.visible = false
    this.isVisible = false

    this.cutsceneContainer = new PIXI.Container();
    this.characterContainer = new PIXI.Container();
    this.clickableContainer = new PIXI.Container();

    this.characterSprite = new PIXI.Sprite()

    this.clickableContainer.position.set(0, 0);

    this.cinematicText = new PIXI.Text('', {
      align: 'center',
      fontFamily: '"Erben Gothic", serif',
      fill: dialogConfig.colors.name,
      fontSize : 36
    })
    this.cinematicText.anchor.set(0.5, 1);
    // this.cinematicText.y = this.screenHeight - 20;
    this.cinematicText.x = this.screenWidth / 2;

    this.createDialogBox();
    this.setupInteractivity();
    this.hide();

    this.container.addChild(this.cinematicText)

    // Load the dialog box texture after initialization
    this.initializeDialogBoxTexture();
  }

  private async initializeDialogBoxTexture(): Promise<void> {
    try {
      this.dialogBoxTexture = await this.loadImage('assets/misc/dialog-box.png');
      // Create the sprite now that texture is loaded
      if (this.dialogBoxTexture) {
        this.dialogBoxSprite = new PIXI.Sprite(this.dialogBoxTexture);
        // Insert the sprite at the correct position in the container
        const backgroundIndex = this.container.getChildIndex(this.background);
        this.container.addChildAt(this.dialogBoxSprite, backgroundIndex);
        
        // Force an update to use the sprite now that it's loaded
        this.lastDialogSize = { width: 0, height: 0 }; // Reset to force update
        this.updateDialogSize();
        
        console.log('Dialog box texture loaded successfully');
      }

      // Load choice selector arrow
      this.choiceSelectorTexture = await this.loadImage('assets/ui/choices_selector.png');

      // Pre-generate gradient texture for choices
      this.choiceGradientTexture = this.createChoiceGradientTexture();
    } catch (error) {
      console.warn('Failed to load dialog box texture, using fallback:', error);
    }
  }

  private createDialogBox(): void {
    // Dialog box background (fallback graphics for when image isn't loaded)
    this.background = new PIXI.Graphics();

    // Create invisible interaction area that will be positioned on top
    this.interactionArea = new PIXI.Graphics();
    this.interactionArea.interactive = true;
    this.interactionArea.cursor = 'pointer';
    this.interactionArea.alpha = 0; // Completely invisible

    // Now call updateDialogSize after both graphics objects are created
    this.updateDialogSize();

    // Define consistent padding values
    const verticalSpacing = 16;

    // NPC name text
    const buttonWidth = this.calculateDialogWidth(); // Use same width as buttons
    const isMobile = InputManager.getInstance().isMobile();
    
    // Base sizes
    const nameSize = isMobile ? dialogConfig.font.nameSize - 4 : dialogConfig.font.nameSize + 4; // Bigger on web
    
    this.nameText = new PIXI.Text('NPC Name', {
      // Use placeholder text for proper height calculation
      fontFamily: '"Erben Gothic", serif',
      fontSize: nameSize,
      fill: dialogConfig.colors.name,
      fontWeight: 'bold',
      wordWrap: true,
      wordWrapWidth: buttonWidth,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 2,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'left', // Left align text
    });
    this.nameText.anchor.set(0, 0); // Left aligned, top aligned
    this.nameText.x = 40;
    this.nameText.y = this.topPadding;
    this.nameText.resolution = window.devicePixelRatio || dialogConfig.font.resolution;
    this.nameText.style.padding = 4;
    this.nameText.interactive = false;

    // Dialog text
    let dialogSize = isMobile ? dialogConfig.font.dialogSize : dialogConfig.font.dialogSize + 6;

    this.dialogText = new PIXI.Text('Dialog text will appear here.', {
      // Use placeholder text
      fontFamily: 'Rosarivo, serif',
      fontSize: dialogSize,
      fill: dialogConfig.colors.dialog,
      wordWrap: true,
      wordWrapWidth: buttonWidth, // Use same width as buttons
      leading: 4,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'left', // Left align text
    });
    this.dialogText.anchor.set(0, 0); // Left aligned, top aligned
    this.dialogText.x = 40; 
    this.dialogText.y = this.topPadding + this.nameText.height + verticalSpacing;
    this.dialogText.resolution = window.devicePixelRatio || dialogConfig.font.resolution;
    this.dialogText.style.padding = 6;
    this.dialogText.interactive = false;

    // Choices container
    this.choicesContainer = new PIXI.Container();
    this.choicesContainer.x = 0; // Will be positioned dynamically
    this.choicesContainer.y = this.topPadding + this.nameText.height + verticalSpacing + 60; // Space for dialog text
    this.choicesContainer.interactive = false;

    // Continue instruction
    this.continueText = new PIXI.Text(dialogConfig.text.continue, {
      fontFamily: 'Rosarivo, serif',
      fontSize: isMobile ? dialogConfig.font.continueSize - 4 : dialogConfig.font.continueSize,
      fill: dialogConfig.colors.continue,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'right', // Right align text
    });
    this.continueText.anchor.set(1, 1); // Right aligned, top aligned
    this.continueText.resolution = window.devicePixelRatio || dialogConfig.font.resolution;
    this.continueText.style.padding = 4;
    this.continueText.interactive = false;
    this.continueText.x = this.screenWidth - 50

    // Add all elements to container (order matters for layering)
    
    this.container.addChild(this.cutsceneContainer); // Background images 
    this.container.addChild(this.clickableContainer); // Clickables

    this.container.addChild(this.background); // Fallback graphics
    if (this.dialogBoxSprite) this.container.addChild(this.dialogBoxSprite); // Dialog box sprite

    this.exitInteractionModeText = new PIXI.Text("Keluar", {
      fontFamily: 'Rosarivo, serif',
      fontSize: isMobile ? 16 :25,
      fill: dialogConfig.colors.name,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'right', // Right align text
    });


    this.exitInteractionModeText.eventMode = 'static';
    this.exitInteractionModeText.cursor = 'pointer';

    this.exitInteractionModeText.anchor.set(1, 1);

    this.exitInteractionModeText.x = this.container.width - 20
    this.exitInteractionModeText.y = this.container.height - 20

    // Handle click/tap - use original index for selection
    this.exitInteractionModeText.on('pointerup', async () => {
      if(this.currentDialogTree.nodes != null){
        const node = this.currentDialogTree.nodes[this.currentNodeId];
        this.currentNodeId = node.nextDialogId;
        await this.displayCurrentNode();
      }
    });

    this.cinematicNextText = new PIXI.Text("Next ▶", {
      fontFamily: 'Rosarivo, serif',
      fontSize: isMobile ? 16 :25,
      fill: dialogConfig.colors.name,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'right', // Right align text
    });

    this.cinematicNextText.eventMode = 'static';
    this.cinematicNextText.cursor = 'pointer';

    this.cinematicNextText.anchor.set(1, 1);

    this.cinematicNextText.x = this.screenWidth - 20
    this.cinematicNextText.y = this.screenHeight - 20

    this.cinematicNextText.on('pointerup', async () => {
      if(this.currentDialogTree.nodes != null){
        const node = this.currentDialogTree.nodes[this.currentNodeId];
        this.currentNodeId = node.nextDialogId;
        await this.displayCurrentNode();
      }
    });

    this.container.addChild(this.characterContainer); // Character above background/box
    this.container.addChild(this.nameText);
    this.container.addChild(this.dialogText);
    this.container.addChild(this.choicesContainer);
    this.container.addChild(this.continueText);
    this.container.addChild(this.interactionArea); // Interaction area on top
    this.container.addChild(this.exitInteractionModeText)
    this.container.addChild(this.cinematicNextText)



  }

  private setupInteractivity(): void {
    // Make the main container interactive for general event handling
    this.container.interactive = true;
  }

  private updateDialogSize(hideBox?: boolean, overrideText?: string): void {
    const padding = 20;
    const verticalSpacing = 16;

    // Background spans full screen width
    const boxWidth = this.screenWidth;

    const hasChoices = this.choicesContainer && this.choicesContainer.children.length > 0;

    // Calculate dynamic height based on content
    let contentHeight = this.currentTopPadding; // Top padding

    // Add name text height (use fallback if not available)
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    contentHeight += nameTextHeight + verticalSpacing;

    const textWidth = this.calculateDialogWidth();
    
    // Apply word wrap width
    if (this.dialogText) {
        this.dialogText.style.wordWrap = true;
        this.dialogText.style.wordWrapWidth = textWidth;
    }

    const textToMeasure = overrideText !== undefined ? overrideText : (this.dialogText?.text || '');
    const dialogTextHeight = textToMeasure
      ? this.accuratelyCountTextHeight(textToMeasure, this.dialogText.style)
      : dialogConfig.font.dialogSize + 4;
    contentHeight += dialogTextHeight + verticalSpacing;

    // Add choices height if present
    if (hasChoices) {
      const choicesHeight = this.choicesContainer.height;
      contentHeight += choicesHeight + verticalSpacing;
    }

    // Add space for continue text
    const continueTextHeight = this.continueText?.height || 0;
    contentHeight += continueTextHeight + padding; // Continue text height + bottom padding

    // Use calculated height or minimum height
    const isMobile = InputManager.getInstance().isMobile();
    const defaultHeight = isMobile ? 160 : dialogConfig.box.heightDefault;
    
    let boxHeight = Math.max(contentHeight, defaultHeight);

    // On PC: Add bottom margin and adjust box height
    const bottomMargin = isMobile ? 0 : 20; // 20px margin on PC, 0 on mobile
    
    // Limit dialog height to screen height minus top padding and bottom margin
    if (!isMobile) {
      const maxHeight = this.screenHeight - this.currentTopPadding - bottomMargin;
      if (boxHeight > maxHeight) {
        boxHeight = maxHeight;
      }
    }

    // Add bottom margin to box height so background extends to screen bottom
    const boxHeightWithMargin = boxHeight + bottomMargin;

    // Position at the bottom of the screen, spanning full width
    const x = 0;
    
    // Position based on calculated height (with margin, content stays above bottom)
    let y = this.screenHeight - boxHeightWithMargin;

    // Optimize: Only redraw if size actually changed
    if (this.lastDialogSize.width === boxWidth && this.lastDialogSize.height === boxHeight) {
      return;
    }

    this.lastDialogSize.width = boxWidth;
    this.lastDialogSize.height = boxHeight;

    // Update dialog box sprite or fallback graphics
    const canUseSprite = this.dialogBoxSprite && this.dialogBoxTexture && this.dialogBoxSprite.parent && !hideBox;
    
    if (canUseSprite && this.dialogBoxSprite) {
      // Scale and xposition the dialog box sprite to fit the dialog area
      this.dialogBoxSprite.width = boxWidth;
      this.dialogBoxSprite.height = boxHeightWithMargin;
      this.dialogBoxSprite.x = 0;
      this.dialogBoxSprite.y = 0;
      this.dialogBoxSprite.visible = true;
      
      // Hide the graphics background when using sprite
      this.background.clear();
      this.background.visible = false;
      
      console.log('Using dialog box sprite:', boxWidth, 'x', boxHeight);
    } else {
      // Use graphics background as fallback or when hiding
      this.background.clear();
      this.background.visible = true;
      if (this.dialogBoxSprite) {
        this.dialogBoxSprite.visible = false;
      }
      
      // Draw translucent black background without borders
      if (hideBox) {
        this.background.rect(0, 0, boxWidth, boxHeightWithMargin).fill({ color: 0x000000, alpha: 0 }); // Black with 0% opacity
      } else {
        this.background.rect(0, 0, boxWidth, boxHeightWithMargin).fill({ color: 0x000000, alpha: 0.7 }); // Black with 70% opacity
      }
      
      console.log('Using fallback graphics background. Sprite available:', !!this.dialogBoxSprite, 'Texture available:', !!this.dialogBoxTexture, 'In parent:', !!this.dialogBoxSprite?.parent);
    }

    // Position container
    console.log(`Dialog Positioning: Screen H=${this.screenHeight}, Box H=${boxHeight}, Y=${y}`);
    
    this.container.x = x;
    this.container.y = y;

    // Update interaction area to cover the entire dialog
    this.interactionArea.clear();

    // Check if there are choices to determine interaction area coverage
    const hasChoicesForInteraction =
      this.choicesContainer && this.choicesContainer.children.length > 0;

    if (!hasChoicesForInteraction) {
      // Cover the entire dialog when there are no choices
      this.interactionArea.beginFill(dialogConfig.colors.shadow, 0);
      this.interactionArea.drawRect(0, 0, boxWidth, boxHeight);
      this.interactionArea.endFill();
    }

    // Explicitly set hit area to ensure proper mouse detection
    if (!hasChoicesForInteraction) {
      this.interactionArea.hitArea = new PIXI.Rectangle(0, 0, boxWidth, boxHeight);
    } else {
      this.interactionArea.hitArea = null;
    }

    // Update continue text position with bounds checking

    // Update dialog text wrap width
    if (this.dialogText) {
      const buttonWidth = this.calculateDialogWidth();
      this.dialogText.style.wordWrapWidth = buttonWidth;
    }
  }

  public show(npcName: string, dialog: string): void {
    this.nameText.text = npcName;
    this.dialogText.text = dialog;
    this.continueText.text = dialogConfig.text.continue;
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button when dialog opens
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
    Game.getInstance().getDayDisplay().setVisible(false)
    console.log("DBGXX E")
    Game.getInstance().getVignetteDisplayArc()?.setVisibility(true)
    Game.getInstance().getVignetteDisplayRadial()?.setVisibility(false)

    // Update positioning with consistent spacing and left alignment with buttons
    const verticalSpacing = 16;

    // Align name and dialog text with button area (left-aligned)
    // const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    // const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonAreaStartX = this.currentButtonAreaStartX;
    // const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.currentTopPadding;

    // Left align dialog text
    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    this.dialogText.y = this.currentTopPadding + nameTextHeight + verticalSpacing;

    // Update positions after text change
    this.updateDialogSize(npcName.length > 0 || dialog.length > 0);
    this.updateContinueTextPositionV2();
  }

  private async drawClickable(
    clickableNode: ClickableNode,
    index:number
  ): PIXI.Container {

    let buttonContainer = new PIXI.Container();

    const gifSource = await PIXI.Assets.load("/assets/sprite/small_sparkle.gif");
    const sprite = new GifSprite(gifSource);

    sprite.width = 200
    sprite.height = 200

    sprite.scale.set(100,100)
    sprite.anchor.set(0.5)
    const clickableTexture = await this.loadImage("/assets/sprite/clickable_indicator.png");
    // let sprite = new PIXI.Sprite(gifSource);

    let text = new PIXI.Text(clickableNode.text, {
      fill: 'white',
      fontFamily: dialogConfig.font.family,
      fontSize: 18,
      padding:0,
      fontWeight : 'bold',
      align: 'center', // Left align text
    })

    sprite.width = 60
    sprite.height = 60
    sprite.position.set(0,0)

    text.position.set(35,6)
    // buttonContainer.addChild(text)

    // TODO, ini tinggal include ini
    const bgScaleX = this.screenWidth / this.backgroundSprite.texture?.width;
    const bgScaleY = this.screenHeight / this.backgroundSprite.texture?.height;
    const bgScale = Math.max(bgScaleX, bgScaleY);

    //Utk tau scale.
    //kalau scalenya yg dipilih X, berarti, kiri kananya ada kepotong.
    //kalau scalenya yg dipilih Y, berarti atas bawahnya yang kepotong (unlikely)
    const useX = bgScaleX > bgScaleY

    //TODO figure out yg keptoong berapa persen, buat jadi offsetX
    //TODO figure out yg keptoong berapa persen, buat jadi offsetY (unlikely)
    const scaledWidth = (this.backgroundSprite.texture?.width || 0) * bgScale;
    const scaledHeight = (this.backgroundSprite.texture?.height || 0) * bgScale;
    const cropXPercent = Math.max(0, (scaledWidth - this.screenWidth) / scaledWidth) * 100;
    const cropYPercent = Math.max(0, (scaledHeight - this.screenHeight) / scaledHeight) * 100;

    //TODO tambahin offset ke kode dibawah
    buttonContainer.x = clickableNode.locationX / 100 * this.container.width
    buttonContainer.y = clickableNode.locationY / 100 * this.container.height


    buttonContainer.eventMode = 'static';
    buttonContainer.cursor = 'pointer';
    buttonContainer.addChild(sprite)

    buttonContainer.on('pointerover', () => {
      // this.drawButtonBackground(buttonBackground, currentButtonWidth, currentButtonHeight, true, isSpecial);
      // choiceText.tint = isSpecial ? 0xffffff : dialogConfig.colors.hover; // White for special on hover
      // if (arrowSprite) arrowSprite.visible = true;
    });

    buttonContainer.on('pointerout', () => {
      // this.drawButtonBackground(buttonBackground, currentButtonWidth, currentButtonHeight, false, isSpecial);
      // choiceText.tint = isSpecial ? 0xffd700 : dialogConfig.colors.white; // Restore original colors
      // if (arrowSprite) arrowSprite.visible = false;
    });

    // Handle click/tap - use original index for selection
    buttonContainer.on('pointerup', async () => {
      await this.selectClickable(index);
    });
    
    return buttonContainer;
  }

  private async setupClickables(node){

    this.clickableContainer.removeChildren()

    this.clickableContainer.x = this.container.getLocalBounds().x
    this.clickableContainer.y = this.container.getLocalBounds().y
    this.clickableContainer.width = this.container.getLocalBounds().width
    this.clickableContainer.height = this.container.getLocalBounds().height

    for(let idx in node.clickables){
      let c = node.clickables[idx]
      let clickable = await this.drawClickable(c, idx as number)

      this.clickableContainer.addChild(clickable)
    }

    const debugBg = new PIXI.Graphics();
    debugBg.rect(0, 0, this.cutsceneContainer.getLocalBounds().width,  this.cutsceneContainer.getLocalBounds().height); // Match the width/height you expect
    debugBg.fill({ color: 0xfff000, alpha: 0 }); // Semi-transparent red
    // this.clickableContainer.addChild(debugBg);
    this.cutsceneContainer.addChild(debugBg)
  }

  public async showDialogTree(dialogTree: DialogTree, npcName?: string): Promise<void> {
    this.currentDialogTree = dialogTree;
    this.currentNodeId = dialogTree.startDialogId;
    this.currentNPCName = npcName || null;
    await this.displayCurrentNode();
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
    Game.getInstance().getDayDisplay().setVisible(false)
    Game.getInstance().getVignetteDisplayArc()?.setVisibility(true)
    console.log("DBGXX A")
    Game.getInstance().getVignetteDisplayRadial()?.setVisibility(false)
  }

  public async showInitialDayDialog(dialogTree: DialogTree): Promise<void> {
    // Show initial day dialog without an NPC context
    this.currentDialogTree = dialogTree;
    this.currentNodeId = dialogTree.startDialogId;
    this.currentNPCName = null; // No NPC for initial day dialogs
    await this.displayCurrentNode();
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
    Game.getInstance().getDayDisplay().setVisible(false)
    Game.getInstance().getVignetteDisplayArc()?.setVisibility(true)

    Game.getInstance().getVignetteDisplayRadial()?.setVisibility(false)
  }

  async fadeCinematicText(newText:string) {
    const isMobile = InputManager.getInstance().isMobile();
    let nextText = new PIXI.Text(newText, {
      fill: 'white',
      fontFamily: dialogConfig.font.family,
      fontSize: isMobile ? 24 : 36,
      padding:0,
      fontWeight : 'bold',
      align: 'center', // Left align text
    })

    console.log("ACCD adding next text")

    nextText.anchor.set(0.5, 0.5);
    nextText.x = this.screenWidth / 2;
    nextText.y = 100
    // nextText.y = this.container.height / 2;

    console.log("ADFA", this.container.height)
    console.log("ADFA", this.container.height, nextText.y)

    // nextText.y = this.screenHeight * 3 / 4;
    nextText.alpha = 0

    const fadeDuration = 0.5; // seconds

    gsap.to(this.cinematicText, {
      alpha: 0,
      duration: fadeDuration,
      onComplete: () => {
        if(this.cinematicText){
          this.cinematicText.destroy(); // Clean up memory
        }
        this.cinematicText = nextText; // Update reference
        this.container.addChild(nextText);
        gsap.to(nextText, {
          alpha: 1,
          duration: fadeDuration
        });

      }
    });

  }

  // Assuming you have this.backgroundContainer (a Pixi.Container)
  async fadeBackground(newTexture:string, overrideInterval?:number) {
    // 1. Create the new sprite

    const backgroundTexture = await this.loadImage(newTexture);

    const nextSprite = new PIXI.Sprite(backgroundTexture);
    nextSprite.alpha = 0; // Start invisible

    // 2. Apply your "Contain" scaling logic
    const bgScale = Math.max(
        this.screenWidth / backgroundTexture.width,
        this.screenHeight / backgroundTexture.height
    );
    nextSprite.scale.set(bgScale);

    // Center it
    nextSprite.x =
        -this.container.x + (this.screenWidth - nextSprite.width) / 2;
    nextSprite.y =
        -this.container.y + (this.screenHeight - nextSprite.height) / 2;

    console.log("ACCD adding next sprite",)
    this.cutsceneContainer.addChild(nextSprite);

    // 3. The Crossfade Animation
    const fadeDuration = overrideInterval ? overrideInterval : 1; // seconds
    let oldBackground = this.backgroundSprite

    // Fade in the new sprite
    gsap.to(nextSprite, {
      alpha: 1,
      duration: fadeDuration,
      onComplete:()=>{
        this.backgroundSprite = nextSprite
        oldBackground?.destroy()
      }
    });
  }

  async fadeCharacter(newTexture:string) {
    // 1. Create the new sprite
    const newCharacterTexture = await this.loadImage(newTexture);

    const nextSprite = new PIXI.Sprite(newCharacterTexture);
    nextSprite.alpha = 0; // Start invisible

    // 3. The Crossfade Animation
    const fadeDuration = 0; // seconds
    // const fadeDuration = newTexture === this.previousCharacterImage ? 0 : 0.2; // seconds

    // Scale character to fit nicely on the right side
    const isMobile = InputManager.getInstance().isMobile();
    const maxCharacterHeight = this.screenHeight * (isMobile ? 1 : 0.9); // Larger on mobile
    const maxCharacterWidth = this.screenWidth * (isMobile ? 0.6 : 1.0); // Allow more width on mobile
    const charScale = Math.min(
      maxCharacterHeight / newCharacterTexture.height,
      maxCharacterWidth / newCharacterTexture.width,
    );
    nextSprite.scale.set(charScale);

    // Position character on right side, accounting for container position
    const rightSideX = this.screenWidth - nextSprite.width + 5; // 50px from right edge
    nextSprite.x = -this.container.x + rightSideX;
    // Position character so its bottom is at the bottom of the screen (y=0 relative to screen)
    nextSprite.y =
      -this.container.y + (this.screenHeight - nextSprite.height);

    let oldSprite = this.characterSprite

    console.log("CTP fade call", nextSprite.width)
    this.updateContinueTextPositionV2(nextSprite.width)

    if(!this.characterSprite){
      this.characterSprite = nextSprite
    }

    // Fade in the new sprite
    gsap.to(nextSprite, {
      alpha: 1,
      duration: fadeDuration,
      onComplete: () => {
        // if(this.backgroundSprite){
        //   this.backgroundSprite.destroy(); // Clean up memory
        // }
        console.log("FADING IN COMPLETE")
        this.characterSprite = nextSprite; // Update reference
      }
    });

    for(let child of this.characterContainer.children){
      gsap.to(child, {
        alpha: 0,
        duration: fadeDuration,
        onComplete: () => {
          console.log("FADING OUT COMPLETE")

          if(child){
            child?.destroy()
          }

        }
      });
    }

    this.characterContainer.addChild(nextSprite);

  }

  async fadeText(newTexture:string) {

  }

  public async showSimpleDialog(
    speaker: string,
    text: string,
    npcName?: string,
    characterImage?: string,
    backgroundImage?: string,
    isEnd?: boolean,
    itemId?: string,
  ): Promise<void> {
    this.currentNPCName = npcName || null;
    this.previousCharacterImage = this.currentCharacterImage
    this.currentCharacterImage = characterImage || null;
    this.currentDialogTree = null;
    this.currentNodeId = null;

    // Reset containers
    this.cutsceneContainer.removeChildren();
    this.characterContainer.removeChildren();
    this.choicesContainer.removeChildren();
    this.clickableContainer.removeChildren();


    this.nameText.text = speaker;
    this.dialogText.text = text;
    // Set appropriate continue text based on whether this was an ending dialog
    this.continueText.text = isEnd ? dialogConfig.text.continueEnd : dialogConfig.text.continue;
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
    Game.getInstance().getDayDisplay().setVisible(false)
    Game.getInstance().getVignetteDisplayArc()?.setVisibility(true)
    console.log("DBGXX C")
    Game.getInstance().getVignetteDisplayRadial()?.setVisibility(false)

    // Update positioning
    const verticalSpacing = 16;
    // const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    // const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonAreaStartX = this.currentButtonAreaStartX; 
    // const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.currentTopPadding;

    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    this.dialogText.y = this.currentTopPadding + nameTextHeight + verticalSpacing;

    this.setupSimpleDialogClick();
    this.updateDialogSize();
    this.updateContinueTextPositionV2();

    // Setup backdrop images if provided
    await this.setupSimpleDialogImages(characterImage, backgroundImage);

    if(itemId){
      let inventory = new InventoryDisplay(this.screenWidth, this.screenHeight);
      let items = itemsConfig
      let selectedItem = items.find(obj=>obj.id === itemId)
      if(selectedItem){
        inventory.showInspectPopup(selectedItem)
      }
    }

  }

  public async showItemPickupDialog(itemId: string, quantity: number): Promise<void> {
    const storyManager = StoryManager.getInstance();
    const itemConfig = await import('@/config/ItemsConfig').then(m => m.getItemConfig(itemId));

    if (!itemConfig) {
      console.warn(`Item config not found for itemId: ${itemId}`);
      return;
    }

    // Add item to inventory
    storyManager.addItem(itemId, quantity);

    // Create pickup message
    const message = itemConfig.dialog || `${quantity}x ${itemConfig.name} acquired!`;

    // Reset containers
    this.cutsceneContainer.removeChildren();
    this.clickableContainer.removeChildren();
    this.characterContainer.removeChildren();
    this.choicesContainer.removeChildren();

    this.nameText.text = ''; // No speaker for item pickup
    this.dialogText.text = message;
    this.continueText.text = 'Click to continue';
    this.isVisible = true;
    this.container.visible = true;

    // Hide interaction button
    InputManager.getInstance().setInteractButtonVisible(false);
    InputManager.getInstance().setMobileControlsVisible(false);
    Game.getInstance().getDayDisplay().setVisible(false)
    Game.getInstance().getVignetteDisplayArc()?.setVisibility(true)
    console.log("DBGXX D")
    Game.getInstance().getVignetteDisplayRadial()?.setVisibility(false)

    // Update positioning
    const verticalSpacing = 16;
    // const BUTTON_MAX_WIDTH = 600;
    // On mobile landscape, use more width (90%) to save vertical space
    // const widthRatio = (this.screenWidth < 768 || InputManager.getInstance().isMobile()) ? 0.9 : 0.6;
    
    const buttonAreaStartX = this.currentButtonAreaStartX; 
    // const buttonAreaStartX = (this.screenWidth - buttonWidth) / 2;

    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.currentTopPadding;

    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    this.dialogText.y = this.currentTopPadding + nameTextHeight + verticalSpacing;

    // Make dialog box transparent/hidden for item pickup
    this.updateDialogSize(true); // true = hide box
    this.updateContinueTextPositionV2();

    // Setup click to close (non-skippable means they must click, but it still closes on click)
    this.setupSimpleDialogClick();
  }

  private async setupSimpleDialogImages(
    characterImage?: string,
    backgroundImage?: string,
  ): Promise<void> {
    // Clear existing backdrop content
    this.cutsceneContainer.removeChildren();
    this.characterContainer.removeChildren();
    this.backgroundSprite = null;

    // Load and display background image
    if (backgroundImage) {
      try {
        const backgroundTexture = await this.loadImage(backgroundImage);
        this.backgroundSprite = new PIXI.Sprite(backgroundTexture);

        // Scale background to cover entire viewport (like CSS background-size: cover)
        const bgScaleX = this.screenWidth / backgroundTexture.width;
        const bgScaleY = this.screenHeight / backgroundTexture.height;
        const bgScale = Math.max(bgScaleX, bgScaleY);
        this.backgroundSprite.scale.set(bgScale);

        // Position background to start from absolute screen coordinates (0,0)
        // Since cutsceneContainer is child of container which is positioned, we need to offset
        this.backgroundSprite.x =
          -this.container.x + (this.screenWidth - this.backgroundSprite.width) / 2;
        this.backgroundSprite.y =
          -this.container.y + (this.screenHeight - this.backgroundSprite.height) / 2;

        // Add a stronger overlay for more atmospheric, spooky effect
        this.backgroundSprite.alpha = 1.0;

        this.cutsceneContainer.addChild(this.backgroundSprite);
      } catch (error) {
        console.warn('Failed to setup background image:', error);
      }
    }

    // Load and display character image
    if (characterImage) {
      try {
        this.fadeCharacter(characterImage)
        console.log("FADEX", characterImage)
        // const characterTexture = await this.loadImage(characterImage);
        // this.characterSprite = new PIXI.Sprite(characterTexture);
        //
        // // Scale character to fit nicely on the right side
        // const isMobile = InputManager.getInstance().isMobile();
        // const maxCharacterHeight = this.screenHeight * (isMobile ? 1 : 0.8); // Larger on mobile
        // const maxCharacterWidth = this.screenWidth * (isMobile ? 0.6 : 0.4); // Allow more width on mobile
        // const charScale = Math.min(
        //   maxCharacterHeight / characterTexture.height,
        //   maxCharacterWidth / characterTexture.width,
        // );
        // this.characterSprite.scale.set(charScale);
        //
        // // Position character on right side, accounting for container position
        // const rightSideX = this.screenWidth - this.characterSprite.width - 50; // 50px from right edge
        // this.characterSprite.x = -this.container.x + rightSideX;
        // // Position character so its bottom is at the bottom of the screen (y=0 relative to screen)
        // this.characterSprite.y =
        //   -this.container.y + (this.screenHeight - this.characterSprite.height);
        //
        // this.characterContainer.addChild(this.characterSprite);
      } catch (error) {
        console.warn('Failed to setup character image:', error);
      }
    }
  }

  private setupSimpleDialogClick(): void {
    // Remove any existing event handlers
    this.interactionArea.off('pointerup');
    this.interactionArea.off('pointerover');
    this.interactionArea.off('pointerout');

    // Make the interaction area active
    this.interactionArea.interactive = true;
    this.interactionArea.cursor = 'pointer';
    this.interactionArea.alpha = 0;

    // Add click handler to close the dialog
    this.interactionArea.on('pointerup', () => {
      this.hide(); // Use hide() instead of hideWithoutRecording() to process any queued actions
    });

    // Add visual feedback
    this.interactionArea.on('pointerover', () => {
      this.continueText.tint = dialogConfig.colors.hover;
      if (this.dialogBoxSprite?.visible) {
        this.dialogBoxSprite.alpha = 0.95;
      } else {
        this.background.alpha = 0.98;
      }
    });

    this.interactionArea.on('pointerout', () => {
      this.continueText.tint = dialogConfig.colors.white;
      if (this.dialogBoxSprite?.visible) {
        this.dialogBoxSprite.alpha = 1.0;
      } else {
        this.background.alpha = 1.0;
      }
    });
  }

  private async initiateDialogAnimation(completeDialog:string){
    if(previousDialogTimeoutId){
      clearTimeout(previousDialogTimeoutId)
    }
    this.dialogText.text = ""
    this.isTyping = true
    this.playDialogAnimation(completeDialog)
  }

  private async playDialogAnimation(completeDialog:string){

    const textToType = completeDialog
    const typingSpeed = 5; // Speed in milliseconds (lower is faster)

    if (this.dialogText.text.length < textToType.length) {
      this.dialogText.text += textToType.charAt(this.dialogText.text.length);
      previousDialogTimeoutId = window.setTimeout(()=>this.playDialogAnimation(completeDialog), typingSpeed);
    }else{
      this.isTyping = false
      // Show choices after typing is complete if there are any
      if (this.choicesContainer && this.choicesContainer.children.length > 0) {
        this.choicesContainer.visible = true;
      }
    }

  }

  private async displayCurrentNode(): Promise<void> {
    if (!this.currentDialogTree || !this.currentNodeId) return;

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) return;

    this.previousCharacterImage = this.currentCharacterImage
    this.currentCharacterImage = node.characterImage || null;

    // Clear any existing choice timeout
    this.clearChoiceTimeout();

    // Play the sound effect if specified in the dialog node
    if (node.sfx) {
      const soundManager = SoundManager.getInstance();
      // Add 'sfx_' prefix to soundEffect ID when playing
      soundManager.play(`sfx_${node.sfx}`);
      console.log(`Playing dialog sound effect: ${node.sfx}`);
    }else{
      const soundManager = SoundManager.getInstance();
      soundManager.play(`sfx_click`);
    }

    // Check for victory/game over triggers on node display
    if (node.isVictory) {
      // Show the dialog first, then trigger victory when it ends
      // We'll handle this in the continue/close logic
    }
    
    // Left-justify choices container to match dialog text
    if (this.choicesContainer) {
        this.choicesContainer.x = this.currentButtonAreaStartX; // Same as buttonAreaStartX
    }

    if (node.isGameOver) {
      // Show the dialog first, then trigger game over when it ends
      // We'll handle this in the continue/close logic
    }

    // Define consistent dimensions
    // Define consistent dimensions
    const buttonWidth = this.calculateDialogWidth();
    const verticalSpacing = 16;
    const buttonAreaStartX = this.currentButtonAreaStartX;

    this.nameText.text = node.speaker;
    
    // Update text wrap width immediately after setting text
    this.nameText.style.wordWrapWidth = buttonWidth;
    this.dialogText.style.wordWrapWidth = buttonWidth;

    // PIXI v8 automatically handles text updates when text or style changes

    // Clear previous choices
    this.choicesContainer.removeChildren();

    if (node.choices && node.choices.length > 0) {
      this.createChoices(node.choices);
      // Hide choices initially, show them after typing finishes
      this.choicesContainer.visible = false;
      this.continueText.text = dialogConfig.text.continueChoice;
      
      // Setup timeout for hidden timeout choice if present
      this.setupChoiceTimeout(node.choices);
    } else {
      this.continueText.text = node.isEnd
        ? dialogConfig.text.continueEnd
        : dialogConfig.text.continue;
    }

    // Start dialog animation after choices are setup (so if it finishes instantly, choices are ready)
    this.initiateDialogAnimation(node.text)

    // Update positioning using the already defined variables
    this.nameText.x = buttonAreaStartX;
    this.nameText.y = this.currentTopPadding;

    this.dialogText.x = buttonAreaStartX;
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;

    // Use full node text for height calculation to ensure consistent box size
    const dialogTextHeight = node.text
      ? this.estimateTextHeight(node.text, buttonWidth, dialogConfig.font.dialogSize)
      : dialogConfig.font.dialogSize + 4;

    this.dialogText.y = this.currentTopPadding + nameTextHeight + verticalSpacing;
    
    this.choicesContainer.y =
      this.currentTopPadding + nameTextHeight + verticalSpacing + dialogTextHeight + verticalSpacing;

    this.setupContinueClick();
    this.updateDialogSize(node.speaker.length === 0 && node.text.length === 0 && !node.choices, node.text);

    // Setup backdrop images after dialog positioning is calculated
    await this.setupCutsceneImages(node);

    if (node.storyAction) {
      this.queueAction({
        type: node.storyAction.type as QueuedAction['type'],
        value: node.storyAction.value,
        customFunction: node.storyAction.customFunction,
      });
    }

    if(node.storyActions){
      for(let action of node.storyActions){
        this.queueAction({
          type: action.type as QueuedAction['type'],
          value: action.value,
          customFunction: action.customFunction,
        });
      }
    }

    if (node.itemReward){

      if (node.itemReward) {

        // this.showItemPickupDialog(node.itemReward.itemId, 1)

        const storyManager = StoryManager.getInstance();
        storyManager.addItem(node.itemReward.itemId, node.itemReward.quantity);
        console.log(
          `Item reward: ${node.itemReward.quantity}x ${node.itemReward.itemId}`,
        );

        // let inventory = new InventoryDisplay(this.screenWidth, this.screenHeight);
        let items = itemsConfig
        let selectedItem = items.find(obj=>obj.id === node.itemReward?.itemId)
        if(selectedItem){
          // inventory.showInspectPopup(selectedItem)
        }

      }

    }

    if (node.bpm){
      Game.getInstance().setVignetteDisplayBPM(node.bpm)
    }

    //Default visiblity of the menu
    this.nameText.visible = true
    this.dialogText.visible = true
    this.dialogBoxSprite.visible = true
    this.continueText.visible = true
    this.exitInteractionModeText.visible = false
    this.cinematicNextText.visible = false

    // Check if node has choices - if so, overlay elements must not block clicks on choice buttons
    const hasChoices = node.choices && node.choices.length > 0;

    //Default interaction of the menu
    this.interactionArea.eventMode = hasChoices ? 'none' : 'static'
    this.nameText.eventMode = hasChoices ? 'none' : 'static'
    this.dialogText.eventMode = hasChoices ? 'none' : 'static'
    this.dialogBoxSprite.eventMode = hasChoices ? 'none' : 'static'
    this.continueText.eventMode = hasChoices ? 'none' : 'static'
    this.exitInteractionModeText.eventMode = 'none'
    this.cinematicNextText.eventMode = 'none'
    this.cinematicText.visible = false

    //Interaction and visibility if there is clickables
    if(node.clickables){
      this.nameText.visible = false
      this.dialogText.visible = false
      this.dialogBoxSprite.visible = false
      this.continueText.visible = false
      this.exitInteractionModeText.visible = true

      this.interactionArea.eventMode = 'none'
      this.nameText.eventMode = 'none'
      this.dialogText.eventMode = 'none'
      this.dialogBoxSprite.eventMode = 'none'
      this.continueText.eventMode = 'none'
      this.exitInteractionModeText.eventMode = 'static'
      this.cinematicNextText.eventMode = 'none'

    }

    if(node.characterImage){
      this.characterSprite.visible = true
    }else{
      this.characterSprite.visible = false
    }

    //Interaction and visibility if there is clickables
    if(node.cinematic){
      this.cinematicText.visible = true
      this.nameText.visible = false
      this.dialogText.visible = false
      this.dialogBoxSprite.visible = false
      this.continueText.visible = false

      this.nameText.eventMode = 'none'
      this.dialogText.eventMode = 'none'
      this.dialogBoxSprite.eventMode = 'none'
      this.continueText.eventMode = 'none'
      this.exitInteractionModeText.eventMode = 'none'
      this.interactionArea.eventMode = 'none'

      setTimeout(()=>{
        this.cinematicNextText.eventMode = 'static'
        this.cinematicNextText.visible = true
        this.interactionArea.eventMode = 'static'
      },1000)

      // this.cinematicText.text = node.text
      this.fadeCinematicText(node.text)

    }

    this.setupClickables(node)

    console.log("OCST TERPANGGIL")
    this.updateContinueTextPositionV2(); // Update position after text changes



  }

  private updateContinueTextPosition(): void {
    if (!this.continueText) return;

    const padding = 30;
    const buttonWidth = this.calculateDialogWidth();

    // Calculate dynamic height based on content
    let contentHeight = this.currentTopPadding; // Top padding
    const nameTextHeight = this.nameText?.height || dialogConfig.font.nameSize + 4;
    contentHeight += nameTextHeight + 16; // Name + spacing

    const dialogTextHeight = this.dialogText?.text
      ? this.estimateTextHeight(this.dialogText.text, buttonWidth, dialogConfig.font.dialogSize)
      : dialogConfig.font.dialogSize + 4;
    contentHeight += dialogTextHeight + 16; // Dialog text + spacing

    if (this.choicesContainer && this.choicesContainer.children.length > 0) {
      const choicesHeight = this.choicesContainer.height;
      contentHeight += choicesHeight + 16; // Choices + spacing
    }

    const continueTextHeight = this.continueText?.height || dialogConfig.font.continueSize + 4;
    const boxHeight = Math.max(
      contentHeight + continueTextHeight + padding,
      dialogConfig.box.heightDefault,
    );

    // Align continue text to the right edge of button area, but keeping it safe from NPC on right
    const buttonAreaStartX = this.currentButtonAreaStartX;
    const buttonAreaEndX = buttonAreaStartX + buttonWidth;

    this.continueText.x = buttonAreaEndX;
    this.continueText.y = boxHeight - this.continueText.height - padding;
  }

  private updateContinueTextPositionV2(customWidth?:number): void {

    console.log("CTP",this.characterSprite, this.characterSprite?.visible, this.characterSprite?.width)
    console.log("CTP dim", customWidth ? customWidth : this.characterSprite?.width)

    const isMobile = InputManager.getInstance().isMobile()
    this.continueText.anchor.set(1,1)
    if(this.characterSprite?.visible){
      console.log("CTPx2",this.characterSprite, this.container.width - (customWidth ? customWidth : this.characterSprite?.width), this.container.height - 20)
      // this.continueText.x = this.screenWidth - (customWidth ? customWidth : this.characterSprite?.width) + 50
      this.continueText.x = this.calculateDialogWidth() + (isMobile ? 25 :100);
      this.continueText.y = -this.container.y + (this.screenHeight - 15)
      // console.log("CTP setting visible", this.characterSprite?.visible, this.continueText.x, this.continueText.y)
    }else{
      console.log("CTPx2",this.characterSprite, this.container.width - 20, this.container.height - 20)
      this.continueText.x = this.screenWidth - 50
      this.continueText.y = -this.container.y + (this.screenHeight - 15)
      // console.log("CTP setting invisible", this.characterSprite?.visible, this.continueText.x, this.continueText.y)
    }

    this.cinematicNextText.x = this.screenWidth-10
    this.cinematicNextText.y = -this.container.y + (this.screenHeight - 15)

    this.exitInteractionModeText.x = this.screenWidth-10
    this.exitInteractionModeText.y = -this.container.y + (this.screenHeight - 15)

  }


  private createChoices(choices: DialogChoice[]): void {
    this.choiceTexts = []; // Clear previous choice texts
    this.choiceButtons = []; // Clear previous choice buttons
    this.choiceBackgrounds = []; // Clear previous choice backgrounds

    // Use reasonable width for buttons but center them on screen
    const buttonWidth = this.calculateDialogWidth();
    
    const isMobile = (this.screenWidth < 768 || InputManager.getInstance().isMobile());

    const buttonSpacing = 8;
    const buttonPadding = 10; // Internal button padding

    const storyManager = StoryManager.getInstance();
    let currentY = 0;

    let minimalFontSize = 999;

    // Calculate text height first to determine button height
    const textPadding = buttonPadding * 2;
    const wrapWidth = buttonWidth - textPadding - 40;

    // Calculate minimum text size to be used
    for(let choice of choices){
      let additionalSize = choices.length <= 2;

      let mobileChoiceSizeDefault = dialogConfig.font.choiceSize + (additionalSize ? 4 : 0)
      let webChoiceSizeDefault = dialogConfig.font.choiceSize + (additionalSize ? 6 : 0)
      let choiceFontSize = isMobile ? mobileChoiceSizeDefault : webChoiceSizeDefault;

      if(this.screenWidth < 700){
        choiceFontSize -= 6
      }

      const result = this.estimateChoiceTextHeight(choice.text, wrapWidth, choiceFontSize);
      const tempFontSize = result.textSize

      if(result.textSize < minimalFontSize){
        minimalFontSize = tempFontSize
      }
    }

    choices.forEach((choice, originalIndex) => {
      // Skip hidden timeout choices - they shouldn't be rendered
      if (choice.isHiddenTimeout) {
        return;
      }

      // Check if choice meets requirements
      if (!storyManager.checkChoiceRequirements(choice)) {
        return; // Skip this choice if requirements not met
      }

      // Create button container
      const buttonContainer = this.getPooledChoiceButton() || new PIXI.Container();

      // Create or reuse button background
      let buttonBackground = this.getPooledChoiceBackground();
      if (!buttonBackground) {
        buttonBackground = new PIXI.Graphics();
      }

      // Determine if this is a special choice
      const isSpecial = choice.isSpecial || false;

      // Create temp text to measure size or use estimate
      const result = this.calculateChoiceTextHeight(choice.text, wrapWidth, minimalFontSize);

      const tempTextHeight = result.height
      const tempFontSize = result.textSize
      // console.log("FS SB", isMobile, dialogConfig.font.choiceSize, this.screenWidth < 700, result.textSize)

      // Calculate dynamic button height
      // Minimum height of 32/40, but expand if text needs more space
      // const minHeight = isMobile ? 40 : 32;
      // Add padding for top/bottom (e.g. 10px each)
      const calculatedHeight = tempTextHeight * 1.5
      
      const currentButtonHeight = calculatedHeight;

      // Draw button background with calculated height
      this.drawButtonBackground(buttonBackground, buttonWidth, currentButtonHeight, false, isSpecial);

      // Use pooled text object if available, otherwise create new one
      let choiceText = this.getPooledChoiceText();

      // Use the same width calculation as the dialog text to ensure no overlap
      const safeWidth = this.calculateDialogWidth();
      const currentButtonWidth = safeWidth;

      if (!choiceText) {
        choiceText = new PIXI.Text(choice.text, {
          fontFamily: dialogConfig.font.family,
          fontSize: minimalFontSize,
          fill: dialogConfig.colors.choice,
          wordWrap: true,
          wordWrapWidth: wrapWidth,
          padding: 4,
          dropShadow: {
            color: dialogConfig.colors.shadow,
            blur: 1,
            distance: 1,
            alpha: 1,
            angle: Math.PI / 4,
          },
          align: 'left', // Left align text
        });
        const textMetrics = CanvasTextMetrics.measureText(
          choice.text,
          choiceText.style
        );
        console.log("CTXA", choice.text, textMetrics.height, textMetrics.lines)
        // choiceText.resolution = window.devicePixelRatio || dialogConfig.font.resolution;
      }

      // Left align text in button
      choiceText.anchor.set(0, 0.5); // Left, Value Center
      choiceText.x = 20;
      choiceText.y = currentButtonHeight / 2; 

      // Clear container and add components
      buttonContainer.removeChildren();
      buttonContainer.addChild(buttonBackground);

      // Add choice selector arrow (hidden by default)
      let arrowSprite: PIXI.Sprite | null = null;
      if (this.choiceSelectorTexture) {
         const arrow = new PIXI.Sprite(this.choiceSelectorTexture);
         arrow.anchor.set(0.5, 0.5);
         arrow.x = -24;
         arrow.y = currentButtonHeight / 2;
         arrow.visible = false; // Hidden by default
         
         // Scale arrow 
         const arrowScale = Math.min(24 / arrow.width, 24 / arrow.height);
         arrow.scale.set(arrowScale);
         
         buttonContainer.addChild(arrow);
         arrowSprite = arrow;
      }

      buttonContainer.addChild(choiceText);

      // Position button - align left (0) since container is already positioned
      buttonContainer.x = 0; 
      buttonContainer.y = currentY;
      buttonContainer.interactive = true;
      buttonContainer.cursor = 'pointer';

      // Clear any existing event listeners
      buttonContainer.removeAllListeners();

      // Add hover effects
      buttonContainer.on('pointerover', () => {
        this.drawButtonBackground(buttonBackground, currentButtonWidth, currentButtonHeight, true, isSpecial);
        choiceText.tint = isSpecial ? 0xffffff : dialogConfig.colors.hover; // White for special on hover
        if (arrowSprite) arrowSprite.visible = true;
      });

      buttonContainer.on('pointerout', () => {
        this.drawButtonBackground(buttonBackground, currentButtonWidth, currentButtonHeight, false, isSpecial);
        choiceText.tint = isSpecial ? 0xffd700 : dialogConfig.colors.white; // Restore original colors
        if (arrowSprite) arrowSprite.visible = false;
      });

      // Handle click/tap - use original index for selection
      buttonContainer.on('pointerup', async () => {
        await this.selectChoice(originalIndex);
      });

      // Add to choices container
      this.choicesContainer.addChild(buttonContainer);

      // Store references
      this.choiceTexts.push(choiceText);
      this.choiceButtons.push(buttonContainer);
      this.choiceBackgrounds.push(buttonBackground);

      // Draw initial background (using new width)
      this.drawButtonBackground(buttonBackground, currentButtonWidth, currentButtonHeight, false, isSpecial);

      currentY += currentButtonHeight + buttonSpacing;
    });
  }

  private getPooledChoiceText(): PIXI.Text | null {
    return this.choiceTextPool.pop() || null;
  }

  private getPooledChoiceButton(): PIXI.Container | null {
    return this.choiceButtonPool.pop() || null;
  }

  private getPooledChoiceBackground(): PIXI.Graphics | null {
    return this.choiceBackgroundPool.pop() || null;
  }

  private createChoiceGradientTexture(): PIXI.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 100; // Small width, we will stretch it
    canvas.height = 1; 

    const ctx = canvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 100, 0);
      gradient.addColorStop(0, '#493E2AD1'); // 82% alpha of #493E2A
      gradient.addColorStop(1, '#CBB07000'); // 0% alpha of #CBB070

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 100, 1);
    }

    return PIXI.Texture.from(canvas);
  }



  private drawButtonBackground(
    graphics: PIXI.Graphics,
    width: number,
    height: number,
    isHovered: boolean,
    isSpecial: boolean = false,
  ): void {
    graphics.clear();
    
    // Background
    if (this.choiceGradientTexture) {
        graphics.roundRect(0, 0, width, height, 6).fill({ texture: this.choiceGradientTexture });
    } else {
        // Fallback if gradient texture fails
        const bgColor = isSpecial ? (isHovered ? 0x8b6914 : 0x6d4c0c) : (isHovered ? 0x3e2723 : 0x2c1810);
        graphics.roundRect(0, 0, width, height, 6).fill({ color: bgColor, alpha: 0.9 });
    }

    // Hover Overlay
    if (isHovered) {
      const overlayColor = isSpecial ? 0xffd700 : dialogConfig.colors.hover;
      graphics.roundRect(0, 0, width, height, 6).fill({ color: overlayColor, alpha: 0.1 });
    }

    // Inner Highlight (keep it for depth, but subtle)
    // const innerHighlightColor = isSpecial ? 0xffd700 : dialogConfig.colors.innerBorder;
    // graphics.roundRect(1, 1, width - 2, height - 2, 5).stroke({ width: 1, color: innerHighlightColor, alpha: 0.2 });

    // Sparkle
    if (isSpecial && isHovered) {
      graphics.roundRect(0.5, 0.5, width - 1, height - 1, 5.5).stroke({ width: 1, color: 0xffffff, alpha: 0.4 });
    }
  }


  private estimateChoiceTextHeight(text: string, wrapWidth: number, fontSize: number = dialogConfig.font.dialogSize): {height:number,textSize:number} {
    if (!text || text.trim() === '') return {height : 0, textSize : fontSize };

    // Create a temporary text object to measure height
    const tempText = new PIXI.Text(text, {
      fontFamily: dialogConfig.font.family,
      fontSize: fontSize,
      wordWrap: true,
      wordWrapWidth: wrapWidth,
      leading: 4,
      padding: 4,
      align: 'left',
    });

    const textMetrics = CanvasTextMetrics.measureText(
        text,
        tempText.style
    );
    const rowCount = textMetrics.lines.length;

    console.log("CCHY", rowCount)

// 3. Calculate rows
    if(rowCount >= 2){
      tempText.destroy()
      const tempText2 = new PIXI.Text(text, {
        fontFamily: dialogConfig.font.family,
        fontSize: fontSize * 0.75,
        wordWrap: true,
        wordWrapWidth: wrapWidth,
        leading: 4,
        padding: 4,
        align: 'left',
      });

      const height = tempText2.height;

      const textMetrics = CanvasTextMetrics.measureText(
          text,
          tempText2.style
      );

      console.log("CCHXB", rowCount, fontSize, height, tempText2.style.lineHeight)
      tempText2.destroy();

      return {height:textMetrics.height, textSize : fontSize * 0.75};
    }

    const height = tempText.height;
    tempText.destroy();

    console.log("CCHXA", rowCount, fontSize, height)

    return {height, textSize : fontSize };
  }

  private calculateChoiceTextHeight(text: string, wrapWidth: number, fontSize: number = dialogConfig.font.dialogSize): {height:number,textSize:number} {
    if (!text || text.trim() === '') return {height : 0, textSize : fontSize };

    // Create a temporary text object to measure height
    const tempText = new PIXI.Text(text, {
      fontFamily: dialogConfig.font.family,
      fontSize: fontSize,
      fill: dialogConfig.colors.choice,
      wordWrap: true,
      wordWrapWidth: wrapWidth,
      padding: 4,
      dropShadow: {
        color: dialogConfig.colors.shadow,
        blur: 1,
        distance: 1,
        alpha: 1,
        angle: Math.PI / 4,
      },
      align: 'left', // Left align text
    });

    const textMetrics = CanvasTextMetrics.measureText(
      text,
      tempText.style
    );
    const height = tempText.height;
    tempText.destroy();
    return {height, textSize : fontSize };
  }


  private estimateTextHeight(text: string, wrapWidth: number, fontSize: number = dialogConfig.font.dialogSize): number {
    if (!text || text.trim() === '') return fontSize + 4; 

    // Create a temporary text object to measure height
    const tempText = new PIXI.Text(text, {
      fontFamily: dialogConfig.font.family,
      fontSize: fontSize,
      wordWrap: true,
      wordWrapWidth: wrapWidth,
      leading: 4,
    });

    const lineHeight = tempText.style.lineHeight || tempText.style.fontSize;

// 3. Calculate rows
    const rowCount = Math.round(tempText.height / lineHeight);
    if(rowCount > 2){
      tempText.destroy()
      const tempText2 = new PIXI.Text(text, {
        fontFamily: dialogConfig.font.family,
        fontSize: fontSize * 0.7,
        wordWrap: true,
        wordWrapWidth: wrapWidth,
        leading: 4,
      });
      const height = tempText2.height || fontSize + 4;
      tempText2.destroy();
      return height;
    }

    const height = tempText.height || fontSize + 4;
    tempText.destroy();
    return height;
  }

  private accuratelyCountTextHeight(text: string, styling): number {
    const textMetrics = CanvasTextMetrics.measureText(
      text,
      styling
    );
    return textMetrics.height
  }


  private async loadImage(imagePath: string): Promise<PIXI.Texture> {
    // Check cache first
    if (this.imageCache.has(imagePath)) {
      const cachedTexture = this.imageCache.get(imagePath);
      if (cachedTexture) {
        return cachedTexture;
      }
    }

    try {
      // Load texture from path using PIXI.Assets.load (PIXI v8)
      const texture = await PIXI.Assets.load(imagePath);
      this.imageCache.set(imagePath, texture);
      return texture;
    } catch (error) {
      console.warn(`Failed to load image: ${imagePath}`, error);
      // Return a placeholder or empty texture
      return PIXI.Texture.EMPTY;
    }
  }

  private async setupCutsceneImages(node: DialogNode): Promise<void> {
    // Clear existing backdrop content
    // this.characterContainer.removeChildren();
    this.backgroundSprite = null;
    clearInterval(this.animationInterval)
    clearTimeout(this.animationInterval)

    if(node.backgroundAnimate){

      let sprites = []
      for(let background of node.backgroundAnimate){
        const backgroundTexture = await this.loadImage(background);
        const nextSprite = new PIXI.Sprite(backgroundTexture);

        const bgScaleX = this.screenWidth / backgroundTexture.width;
        const bgScaleY = this.screenHeight / backgroundTexture.height;
        const bgScale = Math.max(bgScaleX, bgScaleY);
        nextSprite.scale.set(bgScale);

        // Position background to start from absolute screen coordinates (0,0)
        // Since cutsceneContainer is child of container which is positioned, we need to offset
        nextSprite.x =
            -this.container.x + (this.screenWidth - nextSprite.width) / 2;
        nextSprite.y =
            -this.container.y + (this.screenHeight - nextSprite.height) / 2;

        sprites.push(nextSprite)

      }

      let idx = 0

      this.animationInterval = setInterval(()=>{
        this.fadeBackground(node.backgroundAnimate[idx%node.backgroundAnimate.length], 0.5)
        // this.cutsceneContainer.addChild(sprites[idx%sprites.length]);
        idx++
      },1500)

    }

    // Load and display background image
    if (node.backgroundImage) {
      try {

        if(node.fade){
          this.fadeBackground(node.backgroundImage)
        }else{

          this.cutsceneContainer.removeChildren();

          const backgroundTexture = await this.loadImage(node.backgroundImage);
          this.backgroundSprite = new PIXI.Sprite(backgroundTexture);

          // Scale background to cover entire viewport (like CSS background-size: cover)
          const bgScaleX = this.screenWidth / backgroundTexture.width;
          const bgScaleY = this.screenHeight / backgroundTexture.height;
          const bgScale = Math.max(bgScaleX, bgScaleY);
          this.backgroundSprite.scale.set(bgScale);

          // Position background to start from absolute screen coordinates (0,0)
          // Since cutsceneContainer is child of container which is positioned, we need to offset
          this.backgroundSprite.x =
              -this.container.x + (this.screenWidth - this.backgroundSprite.width) / 2;
          this.backgroundSprite.y =
              -this.container.y + (this.screenHeight - this.backgroundSprite.height) / 2;

          // Add a stronger overlay for more atmospheric, spooky effect
          this.backgroundSprite.alpha = 1.0;

          this.cutsceneContainer.addChild(this.backgroundSprite);
        }

      } catch (error) {
        console.warn('Failed to setup background image:', error);
      }
    }

    // Load and display character image
    if (node.characterImage) {
      try {

        let gender = Hidash.getSearchParams('gender')

        let formattedCharacterImage = node.characterImage

        if(node.characterImage === "/assets/character/v2/yanf.png" && gender === "male"){
          formattedCharacterImage = "/assets/character/v2/yanm.png"
        }

        // const characterTexture = await this.loadImage(formattedCharacterImage);
        this.fadeCharacter(formattedCharacterImage)
        // this.characterSprite = new PIXI.Sprite(characterTexture);
        //
        // // Scale character to fit nicely on the right side
        // const isMobile = InputManager.getInstance().isMobile();
        // const maxCharacterHeight = this.screenHeight * (isMobile ? 1 : 0.9); // Larger on mobile
        // const maxCharacterWidth = this.screenWidth * (isMobile ? 0.6 : 1.0); // Allow more width on mobile
        // const charScale = Math.min(
        //   maxCharacterHeight / characterTexture.height,
        //   maxCharacterWidth / characterTexture.width,
        // );
        // this.characterSprite.scale.set(charScale);
        //
        // // Position character on right side, accounting for container position
        // const rightSideX = this.screenWidth - this.characterSprite.width + 5; // 50px from right edge
        // this.characterSprite.x = -this.container.x + rightSideX;
        // // Position character so its bottom is at the bottom of the screen (y=0 relative to screen)
        // this.characterSprite.y =
        //   -this.container.y + (this.screenHeight - this.characterSprite.height);
        //
        // this.characterContainer.addChild(this.characterSprite);
      } catch (error) {
        console.warn('Failed to setup character image:', error);
      }
    }else{
      this.characterContainer.removeChildren()
    }
  }

  private returnChoiceTextToPool(textObject: PIXI.Text): void {
    if (this.choiceTextPool.length < this.maxChoicePoolSize) {
      textObject.removeAllListeners();
      textObject.visible = true;
      textObject.parent?.removeChild(textObject);
      this.choiceTextPool.push(textObject);
    } else {
      textObject.destroy();
    }
  }

  private returnChoiceButtonToPool(buttonContainer: PIXI.Container): void {
    if (this.choiceButtonPool.length < this.maxChoicePoolSize) {
      buttonContainer.removeAllListeners();
      buttonContainer.visible = true;
      buttonContainer.parent?.removeChild(buttonContainer);
      this.choiceButtonPool.push(buttonContainer);
    } else {
      buttonContainer.destroy();
    }
  }

  private returnChoiceBackgroundToPool(backgroundGraphics: PIXI.Graphics): void {
    if (this.choiceBackgroundPool.length < this.maxChoicePoolSize) {
      backgroundGraphics.clear();
      backgroundGraphics.parent?.removeChild(backgroundGraphics);
      this.choiceBackgroundPool.push(backgroundGraphics);
    } else {
      backgroundGraphics.destroy();
    }
  }

  /**
   * Setup timeout for hidden timeout choice if one exists
   */
  private setupChoiceTimeout(choices: DialogChoice[]): void {
    // Find the first hidden timeout choice
    const hiddenTimeoutChoice = choices.find(choice => choice.isHiddenTimeout);
    if (!hiddenTimeoutChoice) return;

    const originalIndex = choices.indexOf(hiddenTimeoutChoice);
    const timeoutSeconds = hiddenTimeoutChoice.timeoutSeconds || 5; // Default to 5 seconds

    console.log(`DialogSystem: Setting up hidden timeout choice (index ${originalIndex}) with ${timeoutSeconds}s delay`);

    this.choiceTimeoutId = window.setTimeout(() => {
      console.log(`DialogSystem: Timeout expired, auto-selecting hidden choice (index ${originalIndex})`);
      this.selectChoice(originalIndex);
    }, timeoutSeconds * 1000);
  }

  /**
   * Clear any active choice timeout
   */
  private clearChoiceTimeout(): void {
    if (this.choiceTimeoutId !== null) {
      clearTimeout(this.choiceTimeoutId);
      this.choiceTimeoutId = null;
      console.log('DialogSystem: Cleared choice timeout');
    }
  }

  private async selectClickable(choiceIndex: number): Promise<void> {

    if (!this.currentDialogTree || !this.currentNodeId) {
      console.warn('DialogSystem: No active dialog tree or current node');
      return;
    }

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) {
      console.error(`DialogSystem: Node '${this.currentNodeId}' not found in dialog tree`);
      return;
    }

    if (((!node.clickables || choiceIndex >= node.clickables.length) || choiceIndex < 0)) {
      console.error(
        `DialogSystem: Invalid choice index ${choiceIndex} for node '${this.currentNodeId}'`,
      );
      return;
    }

    const selectedChoice = node.clickables[choiceIndex];

    if (selectedChoice.nextDialogId) {
      if (this.currentDialogTree.nodes[selectedChoice.nextDialogId]) {
        this.currentNodeId = selectedChoice.nextDialogId;
        await this.displayCurrentNode();
      } else {
        console.error(`DialogSystem: Next dialog node '${selectedChoice.nextDialogId}' not found`);
        this.hide();
      }
    }
  }


  private async selectChoice(choiceIndex: number): Promise<void> {
    // Clear timeout when any choice is selected
    this.clearChoiceTimeout();

    if (this.isTyping) {
      console.log('DialogSystem: Ignoring choice selection while typing');
      return;
    }

    if (!this.currentDialogTree || !this.currentNodeId) {
      console.warn('DialogSystem: No active dialog tree or current node');
      return;
    }

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) {
      console.error(`DialogSystem: Node '${this.currentNodeId}' not found in dialog tree`);
      return;
    }

    if (((!node.choices || choiceIndex >= node.choices.length) && (!node.clickables || choiceIndex >= node.clickables.length)) || choiceIndex < 0) {
      console.error(
        `DialogSystem: Invalid choice index ${choiceIndex} for node '${this.currentNodeId}'`,
      );
      return;
    }

    const selectedChoice = node.choices[choiceIndex];

    // Check if this is a special choice that should be unlocked
    if (selectedChoice.isSpecial && this.currentNPCName) {
      const storyManager = StoryManager.getInstance();
      const dialogId = selectedChoice.nextDialogId || this.currentNodeId || 'unknown';
      storyManager.unlockSpecialDialog(dialogId, this.currentNPCName, selectedChoice.specialType);
    }

    // Store victory/game over triggers for after dialog completion
    const pendingVictory = selectedChoice.isVictory;
    const pendingGameOver = selectedChoice.isGameOver;
    const victoryDetail = {
      reason: 'Victory dialog reached!',
      npc: this.currentNPCName,
      dialog: selectedChoice.nextDialogId || this.currentNodeId,
    };
    const gameOverDetail = {
      reason: 'Game over dialog reached!',
      npc: this.currentNPCName,
      dialog: selectedChoice.nextDialogId || this.currentNodeId,
    };

    // Queue story action if present (instead of executing immediately)
    if (selectedChoice.storyAction) {
      this.queueAction({
        type: selectedChoice.storyAction.type as QueuedAction['type'],
        value: selectedChoice.storyAction.value,
        customFunction: selectedChoice.storyAction.customFunction,
      });
    }

    // Queue story action if present (instead of executing immediately)
    if (selectedChoice.storyActions) {
      for(let action of selectedChoice.storyActions){
        this.queueAction({
          type: action.type as QueuedAction['type'],
          value: action.value,
          customFunction: action.customFunction,
        });
      }

    }

    // Queue legacy action if present
    if (selectedChoice.action) {
      this.queueAction({
        type: 'custom',
        customFunction: selectedChoice.action,
      });
    }

    // Handle item reward if present
    if (selectedChoice.itemReward) {
      const storyManager = StoryManager.getInstance();
      storyManager.addItem(selectedChoice.itemReward.itemId, selectedChoice.itemReward.quantity);
      console.log(
        `Item reward: ${selectedChoice.itemReward.quantity}x ${selectedChoice.itemReward.itemId}`,
      );
    }

    // Handle item consumption if present
    if (selectedChoice.consumeItems) {
      const storyManager = StoryManager.getInstance();
      for (const consumeItem of selectedChoice.consumeItems) {
        const success = storyManager.removeItem(consumeItem.itemId, consumeItem.quantity);
        if (success) {
          console.log(
            `Consumed: ${consumeItem.quantity}x ${consumeItem.itemId}`,
          );
        } else {
          console.warn(
            `Failed to consume: ${consumeItem.quantity}x ${consumeItem.itemId} (insufficient quantity)`,
          );
        }
      }
    }

    // Navigate to next dialog or close
    if (selectedChoice.isEnd) {
      // Check for victory/game over triggers before closing
      if (pendingVictory) {
        window.dispatchEvent(new CustomEvent('gameVictory', { detail: victoryDetail }));
      } else if (pendingGameOver) {
        window.dispatchEvent(new CustomEvent('gameOver', { detail: gameOverDetail }));
      }
      this.hide();
    } else if (selectedChoice.nextDialogId) {
      if (this.currentDialogTree.nodes[selectedChoice.nextDialogId]) {
        this.currentNodeId = selectedChoice.nextDialogId;
        await this.displayCurrentNode();
      } else {
        console.error(`DialogSystem: Next dialog node '${selectedChoice.nextDialogId}' not found`);
        this.hide();
      }
    } else {
      // Check for victory/game over triggers before closing
      if (pendingVictory) {
        window.dispatchEvent(new CustomEvent('gameVictory', { detail: victoryDetail }));
      } else if (pendingGameOver) {
        window.dispatchEvent(new CustomEvent('gameOver', { detail: gameOverDetail }));
      }
      this.hide();
    }
  }

  /**
   * Add an action to the queue to be executed after dialog closes
   */
  private queueAction(action: QueuedAction): void {
    this.actionQueue.push(action);
    this.processStateChangesActionQueue()
    console.log('DialogSystem: Queued action:', action.type);
  }

  /**
   * Process all queued actions in order
   */
  private processActionQueue(): void {

    console.log("PROCESSXX AQ START")

    if (this.actionQueue.length === 0) return;

    console.log(`DialogSystem: Processing ${this.actionQueue.length} queued actions`);
    const storyManager = StoryManager.getInstance();

    for (const action of this.actionQueue) {
      try {
        switch (action.type) {
          case 'addCustomValue':
            console.log("CUSTOMVALUES ADDING", action.value)
            if (typeof action.value === 'object' ) {
              storyManager.addCustomValue(action.value.key, action.value.value);
            }
            break;
          case 'addState':
            console.log("CUSTOMSTATE ADDING", action.value)
            if (action.value !== undefined) {
              storyManager.addGameState(action.value+"");
            }
            break;
          case 'removeState':
            if (action.value !== undefined) {
              storyManager.removeGameState(action.value+"");
            }
            break;
          case 'addPoints':
            if (action.value !== undefined) {
              storyManager.addPoints(action.value);
            } else {
              console.warn('DialogSystem: addPoints action missing value');
            }
            break;
          case 'deductPoints':
            if (action.value !== undefined) {
              storyManager.deductPoints(action.value);
            } else {
              console.warn('DialogSystem: deductPoints action missing value');
            }
            break;
          case 'addAffection':
            if (action.value !== undefined) {
              storyManager.addPoints(action.value); // Positive values go to affection
            } else {
              console.warn('DialogSystem: addAffection action missing value');
            }
            break;
          case 'addHostility':
            if (action.value !== undefined) {
              storyManager.deductPoints(action.value); // Positive deduction goes to hostility
            } else {
              console.warn('DialogSystem: addHostility action missing value');
            }
            break;
          case 'deductAffection':
            if (action.value !== undefined) {
              storyManager.addPoints(-action.value); // Negative values reduce affection
            } else {
              console.warn('DialogSystem: deductAffection action missing value');
            }
            break;
          case 'deductHostility':
            if (action.value !== undefined) {
              storyManager.deductPoints(-action.value); // Negative deduction reduces hostility
            } else {
              console.warn('DialogSystem: deductHostility action missing value');
            }
            break;
          case 'custom':
            if (action.customFunction) {
              action.customFunction();
            } else {
              console.warn('DialogSystem: custom action missing customFunction');
            }
            break;
          case 'nextDay':
            console.log('DialogSystem: Advancing to next day (from queue)');
            storyManager.nextDay(action.value);
            break;
          case 'previousDay':
            console.log('DialogSystem: Going to previous day (from queue)');
            storyManager.previousDay();
            break;
          default:
            console.warn(`DialogSystem: Unknown queued action type: ${action.type}`);
        }
      } catch (error) {
        console.error(`DialogSystem: Error executing queued action ${action.type}:`, error);
      }
    }
    console.log("PROCESSXX AQ DONE")

    // Clear the queue after processing
    this.actionQueue = [];
  }

  /**
   * Process all queued actions in order
   */
  private processStateChangesActionQueue(): void {
    if (this.actionQueue.length === 0) return;

    console.log(`DialogSystem: Processing ${this.actionQueue.length} queued actions`);
    const storyManager = StoryManager.getInstance();

    let skippedActions = []

    for (const action of this.actionQueue) {
      try {
        switch (action.type) {
          case 'addCustomValue':
            console.log("CUSTOMVALUES ADDING", action.value)
            if (typeof action.value === 'object' ) {
              storyManager.addCustomValue(action.value.key, action.value.value);
            }
            break;
          case 'addState':
            console.log("CUSTOMSTATE ADDING", action.value)
            if (action.value !== undefined) {
              storyManager.addGameState(action.value+"");
            }
            break;
          case 'removeState':
            if (action.value !== undefined) {
              storyManager.removeGameState(action.value+"");
            }
            break;
          case 'addPoints':
            if (action.value !== undefined) {
              storyManager.addPoints(action.value);
            } else {
              console.warn('DialogSystem: addPoints action missing value');
            }
            break;
          case 'deductPoints':
            if (action.value !== undefined) {
              storyManager.deductPoints(action.value);
            } else {
              console.warn('DialogSystem: deductPoints action missing value');
            }
            break;
          case 'addAffection':
            if (action.value !== undefined) {
              storyManager.addPoints(action.value); // Positive values go to affection
            } else {
              console.warn('DialogSystem: addAffection action missing value');
            }
            break;
          case 'addHostility':
            if (action.value !== undefined) {
              storyManager.deductPoints(action.value); // Positive deduction goes to hostility
            } else {
              console.warn('DialogSystem: addHostility action missing value');
            }
            break;
          default:
            skippedActions.push(action)
        }
      } catch (error) {
        console.error(`DialogSystem: Error executing queued action ${action.type}:`, error);
      }
    }

    // Clear the queue after processing
    this.actionQueue = skippedActions;
  }

  /**
   * Clear the action queue without processing (used for cleanup)
   */
  private clearActionQueue(): void {
    this.actionQueue = [];
  }

  private setupContinueClick(): void {
    // Remove any existing continue click handlers
    this.interactionArea.off('pointerup');
    this.interactionArea.off('pointerover');
    this.interactionArea.off('pointerout');
    this.background.off('pointerup');
    this.background.off('pointerover');
    this.background.off('pointerout');

    if (!this.currentDialogTree || !this.currentNodeId) return;

    const node = this.currentDialogTree.nodes[this.currentNodeId];
    if (!node) return;

    // Only set up click handler for non-choice dialogs
    if (!node.choices || node.choices.length === 0) {
      // Make the interaction area active
      this.interactionArea.interactive = true;
      this.interactionArea.eventMode = 'static'; // Restore event handling for continue clicks
      this.interactionArea.cursor = 'pointer';
      this.interactionArea.alpha = 0; // Completely invisible

      // Define the continue action
      const continueAction = async () => {
        if (this.isTyping) {
          console.log('DialogSystem: Ignoring continue click while typing');
          return;
        }

        // Record interaction before any navigation that might trigger day changes
        this.recordCurrentInteraction();

        if (node.isEnd) {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        } else if (node.nextDialogId) {
          this.currentNodeId = node.nextDialogId;
          await this.displayCurrentNode();
        } else {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        }
      };

      // Add click handler to interaction area - this will catch clicks anywhere on the dialog
      this.interactionArea.on('pointerup', continueAction);

      // Add visual feedback when hovering over the entire dialog
      this.interactionArea.on('pointerover', () => {
        this.continueText.tint = dialogConfig.colors.hover;
        if (this.dialogBoxSprite?.visible) {
          this.dialogBoxSprite.alpha = 0.95; // Slightly more transparent on hover
        } else {
          this.background.alpha = 0.98; // Slightly more opaque on hover for graphics fallback
        }
      });

      this.interactionArea.on('pointerout', () => {
        this.continueText.tint = dialogConfig.colors.white;
        if (this.dialogBoxSprite?.visible) {
          this.dialogBoxSprite.alpha = 1.0; // Normal opacity
        } else {
          this.background.alpha = 1.0; // Normal opacity for graphics fallback
        }
      });
    } else {
      // Remove interactivity when there are choices
      this.interactionArea.interactive = false;
      this.interactionArea.eventMode = 'none'; // Fully disable event blocking so choice buttons can receive clicks
      this.interactionArea.cursor = 'default';
      this.interactionArea.alpha = 0; // Hide when not needed
      this.continueText.tint = dialogConfig.colors.white; // Reset color
      if (this.dialogBoxSprite?.visible) {
        this.dialogBoxSprite.alpha = 1.0; // Reset opacity
      } else {
        this.background.alpha = 1.0; // Reset opacity for graphics fallback
      }
    }
  }

  public async handleInput(
    _up: boolean,
    _down: boolean,
    interact: boolean,
    cancel: boolean = false,
  ): Promise<boolean> {
    if (!this.isVisible) return false;

    if(this.isTyping){
      return false;
    }

    // Handle cancel input - close dialog immediately
    if (cancel) {
      this.hide();
      return true;
    }

    // For simple dialogs (no dialog tree), just close on interact
    if (!this.currentDialogTree) {
      if (interact) {
        this.hide();
        return true;
      }
      return false;
    }

    // For backwards compatibility, still handle interact for non-choice dialogs
    if (interact && this.currentDialogTree && this.currentNodeId) {
      const node = this.currentDialogTree.nodes[this.currentNodeId];
      if (node && (!node.choices || node.choices.length === 0)) {
        // Record interaction before any navigation
        this.recordCurrentInteraction();

        if (node.isEnd) {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        } else if (node.nextDialogId) {
          this.currentNodeId = node.nextDialogId;
          await this.displayCurrentNode();
        } else {
          // Check for victory/game over triggers before closing
          if (node.isVictory) {
            window.dispatchEvent(
              new CustomEvent('gameVictory', {
                detail: {
                  reason: 'You have won the game!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          } else if (node.isGameOver) {
            window.dispatchEvent(
              new CustomEvent('gameOver', {
                detail: {
                  reason: 'You have failed!!',
                  npc: this.currentNPCName,
                  dialog: this.currentNodeId,
                },
              }),
            );
          }
          this.hide();
        }
        return true;
      }


    }



    return false;
  }

  public hide(): void {
    // Record the current interaction first
    this.recordCurrentInteraction();

    // Process all queued actions after dialog interaction is recorded
    this.processActionQueue();

    // Then hide the dialog
    this.hideWithoutRecording();
  }

  private recordCurrentInteraction(): void {
    // Record the interaction with StoryManager
    if (this.currentNPCName && this.currentDialogTree && this.currentNodeId) {
      const node = this.currentDialogTree.nodes[this.currentNodeId];
      if (node) {
        const storyManager = StoryManager.getInstance();

        // Build choices information if available
        let choicesInfo: Array<{ text: string; wasSelected: boolean }> | undefined;
        if (node.choices && node.choices.length > 0) {
          choicesInfo = node.choices.map(choice => ({
            text: choice.text,
            wasSelected: false, // We don't track which choice was selected in this simple implementation
          }));
        }

        storyManager.markNPCInteraction(
          this.currentNPCName,
          this.currentNodeId,
          node.speaker,
          node.text,
          node.characterImage,
          node.backgroundImage,
          node.isEnd,
          choicesInfo,
        );
      }
    }
  }

  private hideWithoutRecording(): void {

    this.isVisible = false;
    this.container.visible = false;
    
    // Show interaction button again when dialog closes
    InputManager.getInstance().setInteractButtonVisible(true);
    InputManager.getInstance().setMobileControlsVisible(true);
    Game.getInstance().getDayDisplay().setVisible(true)
    Game.getInstance().getVignetteDisplayArc()?.setVisibility(false)
    Game.getInstance().getVignetteDisplayRadial()?.setVisibility(true)
    // Clear any active choice timeout
    this.clearChoiceTimeout();

    // Clear any remaining queued actions when dialog is force-closed
    this.clearActionQueue();

    // Clean up all event listeners
    this.container.off('pointerup');
    this.interactionArea.off('pointerup');
    this.interactionArea.off('pointerover');
    this.interactionArea.off('pointerout');
    this.background.off('pointerup');
    this.background.off('pointerover');
    this.background.off('pointerout');
    this.continueText.off('pointerup');
    this.continueText.off('pointerover');
    this.continueText.off('pointerout');

    // Clear backdrop elements
    this.cutsceneContainer.removeChildren();
    this.backgroundSprite = null;


    // Return choice texts to pool and clean up
    this.choiceTexts.forEach(choiceText => {
      this.returnChoiceTextToPool(choiceText);
    });
    this.choiceTexts = [];

    // Return choice buttons to pool and clean up
    this.choiceButtons.forEach(choiceButton => {
      this.returnChoiceButtonToPool(choiceButton);
    });
    this.choiceButtons = [];

    // Return choice backgrounds to pool and clean up
    this.choiceBackgrounds.forEach(choiceBackground => {
      this.returnChoiceBackgroundToPool(choiceBackground);
    });
    this.choiceBackgrounds = [];

    this.choicesContainer.removeChildren();

    // Reset visual states
    this.continueText.tint = dialogConfig.colors.white;
    if (this.dialogBoxSprite) {
      this.dialogBoxSprite.alpha = 1.0;
    }
    this.background.alpha = 1.0;

    // Cancel animation
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Clear current context
    this.currentNPCName = null;
    this.currentCharacterImage = null;
    this.currentDialogTree = null;
    this.currentNodeId = null;
  }

  public isActive(): boolean {
    return this.isVisible;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.updateDialogSize();
  }

  public update(): void {
    if (this.isVisible) {
      // Optimized animation: Use requestAnimationFrame instead of Date.now()
      // for better performance and frame sync
      if (this.animationId === null) {
        const animatePulse = (timestamp: number) => {
          if (!this.isVisible) return;

          const pulse =
            dialogConfig.animation.pulseMin +
            Math.sin(timestamp * dialogConfig.animation.pulseSpeed) *
              dialogConfig.animation.pulseRange;
          this.continueText.alpha = pulse;
          this.cinematicNextText.alpha = pulse;
          this.exitInteractionModeText.alpha = pulse;

          this.animationId = requestAnimationFrame(animatePulse);
        };
        this.animationId = requestAnimationFrame(animatePulse);
      }
    } else if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Calculate safe dialog width to avoid overlapping with NPC
   */
  private calculateDialogWidth(): number {
    const isMobile = InputManager.getInstance().isMobile();
    const buttonAreaStartX = this.currentButtonAreaStartX;
    const paddingRight = isMobile ? 20 : 40;

    const hasNPC = !!this.currentNPCName || !!this.currentCharacterImage || (this.characterContainer && this.characterContainer.children.length > 0);
    
    let textWidth;
    if (hasNPC) { 
        // Give text more space on mobile (up to 60-70% instead of 40%)
        const charWidthRatio = isMobile ? 0.35 : 0.4;
        textWidth = this.screenWidth * (1 - charWidthRatio) - buttonAreaStartX - paddingRight; 
    } else {
        // Full width minus margins
        textWidth = this.screenWidth - buttonAreaStartX - paddingRight; 
    }
    
    return Math.max(textWidth, 150);
  }

  public destroy(): void {
    // Clear any active choice timeout
    this.clearChoiceTimeout();

    // Cancel any running animations
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Clear image cache
    this.imageCache.clear();

    // Destroy all pooled choice texts
    this.choiceTextPool.forEach(text => {
      text.destroy();
    });
    this.choiceTextPool = [];

    // Destroy all pooled choice buttons
    this.choiceButtonPool.forEach(button => {
      button.destroy();
    });
    this.choiceButtonPool = [];

    // Destroy all pooled choice backgrounds
    this.choiceBackgroundPool.forEach(background => {
      background.destroy();
    });
    this.choiceBackgroundPool = [];

    // Destroy current choice texts
    this.choiceTexts.forEach(text => {
      text.destroy();
    });
    this.choiceTexts = [];

    // Destroy current choice buttons
    this.choiceButtons.forEach(button => {
      button.destroy();
    });
    this.choiceButtons = [];

    // Destroy current choice backgrounds
    this.choiceBackgrounds.forEach(background => {
      background.destroy();
    });
    this.choiceBackgrounds = [];

    // Remove all event listeners and destroy the container
    this.container.destroy({ children: true, texture: false });
  }
}
